import { create } from "zustand";
import { z } from "zod";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { isStaleWrite } from "@/lib/staleWrite";
import {
  QuoteResponse,
  QuoteStatus,
  ListQuotesResponseSchema,
  GetQuoteResponseSchema,
  CreateQuoteParams,
  UpdateQuoteParams,
  CreateQuoteResponseSchema,
  UpdateQuoteResponseSchema,
  HydratedItem,
  HydratedCompany,
  HydratedOpportunity,
  Location,
  PublicQuote,
} from "@forgepoint/shared";
import { useItemsStore } from "@/modules/items/itemsStore";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { useLocationsStore } from "@/modules/locations/locationsStore";

interface QuoteLoadingState {
  isLoadingGet: boolean;
  isLoadingDelete: boolean;
  isLoadingSave: boolean;
  isLoadingArchive: boolean;
  getError: string | null;
  deleteError: string | null;
  saveError: string | null;
  archiveError: string | null;
}

/** Options for a single-quote read. */
interface GetQuoteOptions {
  /**
   * Asks the server for the customer-safe projection even when the quote has no
   * stored document. Only callers that want the quote's DATA rather than its
   * document need this - the template editor previewing a template with a real
   * quote's values - because building the projection is not free.
   */
  includePublicQuote?: boolean;
}

interface QuotesStore {
  quotes: Map<string, QuoteResponse>;
  /**
   * Customer-safe projections keyed by quote id, present only for quotes whose
   * stored document renders. The editor preview renders the document from
   * this projection so it shows exactly what the public page shows.
   */
  publicQuotes: Map<string, PublicQuote>;
  quoteLoadingStates: Map<string, QuoteLoadingState>;
  listLoading: boolean;
  listError: string | null;
  total: number;

  fetchQuotes: (params?: {
    status?: QuoteStatus;
    search?: string;
    opportunityId?: string;
    unlinkedOnly?: boolean;
    itemIds?: string[];
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) => Promise<string[]>;
  /**
   * Counts quotes with a line referencing the item (list API with `limit=0`),
   * scoped to the caller's quote-view permissions. Returns 0 on failure.
   */
  countQuotesByItem: (itemId: string) => Promise<number>;
  getQuote: (
    quoteId: string,
    options?: GetQuoteOptions,
  ) => Promise<QuoteResponse | null>;
  resolveQuoteIdByNumber: (quoteNumber: number) => Promise<string | null>;
  deleteQuote: (id: string) => Promise<boolean>;
  createQuote: (
    params: CreateQuoteParams,
  ) => Promise<{ quote: QuoteResponse | null; error: string | null }>;
  updateQuote: (
    id: string,
    params: UpdateQuoteParams,
  ) => Promise<{ quote: QuoteResponse | null; error: string | null }>;
  finalizeQuote: (
    id: string,
  ) => Promise<{ quote: QuoteResponse | null; error: string | null }>;
  createRevision: (
    id: string,
  ) => Promise<{ quote: QuoteResponse | null; existingDraftId?: string }>;
  archiveQuote: (id: string) => Promise<{ success: boolean; error?: string }>;
  unarchiveQuote: (id: string) => Promise<{ success: boolean; error?: string }>;
}

const getInitialLoadingState = (): QuoteLoadingState => ({
  isLoadingGet: false,
  isLoadingDelete: false,
  isLoadingSave: false,
  isLoadingArchive: false,
  getError: null,
  deleteError: null,
  saveError: null,
  archiveError: null,
});

/** Schema for the 409 conflict response when a draft revision already exists. */
const ConflictResponseSchema = z.object({
  existingDraftId: z.string(),
});

/**
 * Merges related entities from quote(s) into their respective stores.
 * Extracts and merges items, companies, opportunities, and locations from quotes.
 */
const mergeRelatedEntities = (
  quotes: QuoteResponse | QuoteResponse[],
): void => {
  const quotesArray = Array.isArray(quotes) ? quotes : [quotes];
  const itemsToMerge: HydratedItem[] = [];
  const companiesToMerge: HydratedCompany[] = [];
  const opportunitiesToMerge: HydratedOpportunity[] = [];
  const locationsToMerge: Location[] = [];

  quotesArray.forEach((quote: QuoteResponse) => {
    if (quote.lines) {
      quote.lines.forEach((line) => {
        if (line.type === "ITEM" && line.item) {
          itemsToMerge.push(line.item);
        }
      });
    }
    if (quote.customerCompany) {
      companiesToMerge.push(quote.customerCompany);
    }
    if (quote.opportunity) {
      opportunitiesToMerge.push(quote.opportunity);
    }
    if (quote.location) {
      locationsToMerge.push(quote.location);
    }
  });

  if (itemsToMerge.length > 0) {
    useItemsStore.getState().mergeItems(itemsToMerge);
  }
  if (companiesToMerge.length > 0) {
    useCompaniesStore.getState().mergeCompanies(companiesToMerge);
  }
  if (opportunitiesToMerge.length > 0) {
    useOpportunitiesStore.getState().mergeOpportunities(opportunitiesToMerge);
  }
  if (locationsToMerge.length > 0) {
    useLocationsStore.getState().mergeLocations(locationsToMerge);
  }
};

export const useRecordQuotesStore = create<QuotesStore>((set, get) => ({
  quotes: new Map(),
  publicQuotes: new Map(),
  quoteLoadingStates: new Map(),
  listLoading: false,
  listError: null,
  total: 0,

  fetchQuotes: async (params?: {
    status?: QuoteStatus;
    search?: string;
    opportunityId?: string;
    unlinkedOnly?: boolean;
    itemIds?: string[];
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    set({ listLoading: true, listError: null });

    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append("status", params.status);
      }
      if (params?.search) {
        queryParams.append("search", params.search);
      }
      if (params?.opportunityId) {
        queryParams.append("opportunityId", params.opportunityId);
      }
      if (params?.unlinkedOnly) {
        queryParams.append("unlinkedOnly", "true");
      }
      if (params?.itemIds && params.itemIds.length > 0) {
        queryParams.append("itemIds", params.itemIds.join(","));
      }
      if (params?.includeArchived) {
        queryParams.append("includeArchived", "true");
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        queryParams.append("offset", String(params.offset));
      }
      const queryString = queryParams.toString();
      const url = queryString ? `quotes?${queryString}` : "quotes";

      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListQuotesResponseSchema.parse(response.data);

        mergeRelatedEntities(data.quotes);

        set((state) => {
          const quotesMap = new Map(state.quotes);
          data.quotes.forEach((quote: QuoteResponse) => {
            quotesMap.set(quote.id, quote);
          });
          return { quotes: quotesMap, listLoading: false, total: data.total };
        });
        return data.quotes.map((quote: QuoteResponse) => quote.id);
      }

      set({ listError: "Failed to fetch quotes", listLoading: false });
      return [];
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch quotes", listLoading: false });
      return [];
    }
  },

  countQuotesByItem: async (itemId: string) => {
    try {
      const queryParams = new URLSearchParams({ itemIds: itemId, limit: "0" });
      const response = await fetchApi(`quotes?${queryParams}`, "GET");
      if (response.ok && response.data) {
        return ListQuotesResponseSchema.parse(response.data).total;
      }
      return 0;
    } catch (error) {
      captureException(error as Error);
      return 0;
    }
  },

  /**
   * Fetches a single quote through the enriched `GET /quotes/:id` endpoint,
   * which resolves the render-ready document fields (`quoteDocument`,
   * `assetUrls`) and `publicQuote` projection the list endpoint omits.
   * Updates the entity and projection maps and returns the fetched quote,
   * or null on failure.
   * @param id - The quote id to fetch
   * @param options - Opts into the projection for a quote without a document
   * @returns The fetched quote, or null if the request failed
   */
  getQuote: async (
    id: string,
    options?: GetQuoteOptions,
  ): Promise<QuoteResponse | null> => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const query = options?.includePublicQuote
        ? "?includePublicQuote=true"
        : "";
      const response = await fetchApi(`quotes/${id}${query}`, "GET");

      if (response.ok && response.data) {
        const data = GetQuoteResponseSchema.parse(response.data);

        // A stale response's embedded hydration is equally stale — skip the
        // related-entity merges along with the quote write.
        if (!isStaleWrite(get().quotes.get(id), data.quote)) {
          mergeRelatedEntities(data.quote);
        }

        set((state) => {
          const quotes = new Map(state.quotes);
          const publicQuotes = new Map(state.publicQuotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          const currentState =
            quoteLoadingStates.get(id) || getInitialLoadingState();
          // The projection follows the quote — a stale copy leaves both untouched.
          if (!isStaleWrite(quotes.get(id), data.quote)) {
            quotes.set(id, data.quote);
            if (data.publicQuote) {
              publicQuotes.set(id, data.publicQuote);
            } else {
              publicQuotes.delete(id);
            }
          }
          quoteLoadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { quotes, publicQuotes, quoteLoadingStates };
        });
        return data.quote;
      }

      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch quote",
        });
        return { quoteLoadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch quote",
        });
        return { quoteLoadingStates };
      });
      return null;
    }
  },

  /**
   * Resolves a quote id from a quote number through the list endpoint (the
   * only lookup the API offers for numbers).
   * @param quoteNumber - The quote number to resolve
   * @returns The matching quote id, or null when no quote matches
   */
  resolveQuoteIdByNumber: async (
    quoteNumber: number,
  ): Promise<string | null> => {
    try {
      const queryParams = new URLSearchParams({
        quoteNumbers: quoteNumber.toString(),
        includeArchived: "true",
      });
      const response = await fetchApi(
        `quotes?${queryParams.toString()}`,
        "GET",
      );
      if (!response.ok || !response.data) {
        return null;
      }
      const data = ListQuotesResponseSchema.parse(response.data);
      return data.quotes[0]?.id ?? null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  deleteQuote: async (id: string) => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi(`quotes/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          quotes.delete(id);
          quoteLoadingStates.delete(id);
          return { quotes, quoteLoadingStates };
        });
        return true;
      }

      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete quote",
        });
        return { quoteLoadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete quote",
        });
        return { quoteLoadingStates };
      });
      return false;
    }
  },

  createQuote: async (params: CreateQuoteParams) => {
    const tempId = "new";
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(tempId) || getInitialLoadingState();
      quoteLoadingStates.set(tempId, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi("quotes", "POST", { body: params });

      if (response.ok && response.data) {
        const data = CreateQuoteResponseSchema.parse(response.data);

        mergeRelatedEntities(data.quote);

        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          quotes.set(data.quote.id, data.quote);
          quoteLoadingStates.delete(tempId);
          return { quotes, quoteLoadingStates };
        });
        return { quote: data.quote, error: null };
      }

      const errorMessage = response.error ?? "Failed to create quote";
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(tempId) || getInitialLoadingState();
        quoteLoadingStates.set(tempId, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(tempId) || getInitialLoadingState();
        quoteLoadingStates.set(tempId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to create quote",
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: "Failed to create quote" };
    }
  },

  updateQuote: async (
    id: string,
    params: UpdateQuoteParams,
  ): Promise<{ quote: QuoteResponse | null; error: string | null }> => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi(`quotes/${id}`, "PUT", { body: params });

      if (response.ok && response.data) {
        const data = UpdateQuoteResponseSchema.parse(response.data);

        // Stale responses skip the related merges too (see getQuote).
        if (!isStaleWrite(get().quotes.get(id), data.quote)) {
          mergeRelatedEntities(data.quote);
        }

        set((state) => {
          const quotes = new Map(state.quotes);
          const publicQuotes = new Map(state.publicQuotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          const currentState =
            quoteLoadingStates.get(id) || getInitialLoadingState();
          if (!isStaleWrite(quotes.get(id), data.quote)) {
            quotes.set(id, data.quote);
            // Only refresh the projection when the save recomputed one; absence
            // means the quote has no document, so keep the existing projection
            // rather than deleting it (unlike getQuote).
            if (data.publicQuote) {
              publicQuotes.set(id, data.publicQuote);
            }
          }
          quoteLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { quotes, publicQuotes, quoteLoadingStates };
        });
        return { quote: data.quote, error: null };
      }

      const errorMessage = response.error ?? "Failed to update quote";
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update quote",
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: "Failed to update quote" };
    }
  },

  finalizeQuote: async (id: string) => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi(`quotes/${id}/status`, "PUT", {
        body: { status: "FINALIZED" },
      });

      if (response.ok && response.data) {
        const data = UpdateQuoteResponseSchema.parse(response.data);

        // Stale responses skip the related merges too (see getQuote).
        if (!isStaleWrite(get().quotes.get(id), data.quote)) {
          mergeRelatedEntities(data.quote);
        }

        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          const currentState =
            quoteLoadingStates.get(id) || getInitialLoadingState();
          if (!isStaleWrite(quotes.get(id), data.quote)) {
            quotes.set(id, data.quote);
          }
          quoteLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { quotes, quoteLoadingStates };
        });
        return { quote: data.quote, error: null };
      }

      const errorMessage = response.error ?? "Failed to finalize quote";
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to finalize quote",
        });
        return { quoteLoadingStates };
      });
      return { quote: null, error: "Failed to finalize quote" };
    }
  },

  createRevision: async (id: string) => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { quoteLoadingStates };
    });

    const setSaveError = (saveError: string) => {
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError,
        });
        return { quoteLoadingStates };
      });
    };

    try {
      const response = await fetchApi(`quotes/${id}/revisions`, "POST");

      if (response.ok && response.data) {
        const data = CreateQuoteResponseSchema.parse(response.data);

        mergeRelatedEntities(data.quote);

        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          const currentState =
            quoteLoadingStates.get(id) || getInitialLoadingState();
          quotes.set(data.quote.id, data.quote);
          quoteLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { quotes, quoteLoadingStates };
        });
        return { quote: data.quote };
      }

      if (response.status === 409) {
        const existingDraftId = ConflictResponseSchema.safeParse(response.data)
          .data?.existingDraftId;
        setSaveError("A draft revision already exists for this quote");
        return { quote: null, existingDraftId };
      }

      setSaveError("Failed to create revision");
      return { quote: null };
    } catch (error) {
      captureException(error as Error);
      setSaveError("Failed to create revision");
      return { quote: null };
    }
  },

  /**
   * Archives a quote by setting archived=true via the update endpoint.
   * @param id - The quote ID to archive
   * @returns Result with success status and optional error message
   */
  archiveQuote: async (id: string) => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingArchive: true,
        archiveError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi(`quotes/${id}`, "PUT", {
        body: { archived: true },
      });

      if (response.ok && response.data) {
        const data = UpdateQuoteResponseSchema.parse(response.data);

        // Stale responses skip the related merges too (see getQuote).
        if (!isStaleWrite(get().quotes.get(id), data.quote)) {
          mergeRelatedEntities(data.quote);
        }

        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          if (!isStaleWrite(quotes.get(id), data.quote)) {
            quotes.set(id, data.quote);
          }
          quoteLoadingStates.set(id, getInitialLoadingState());
          return { quotes, quoteLoadingStates };
        });
        return { success: true };
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        "Failed to archive quote";
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: errorMessage,
        });
        return { quoteLoadingStates };
      });
      return { success: false, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to archive quote",
        });
        return { quoteLoadingStates };
      });
      return { success: false, error: "Failed to archive quote" };
    }
  },

  /**
   * Unarchives a quote by setting archived=false via the update endpoint.
   * @param id - The quote ID to unarchive
   * @returns Result with success status and optional error message
   */
  unarchiveQuote: async (id: string) => {
    set((state) => {
      const quoteLoadingStates = new Map(state.quoteLoadingStates);
      const currentState =
        quoteLoadingStates.get(id) || getInitialLoadingState();
      quoteLoadingStates.set(id, {
        ...currentState,
        isLoadingArchive: true,
        archiveError: null,
      });
      return { quoteLoadingStates };
    });

    try {
      const response = await fetchApi(`quotes/${id}`, "PUT", {
        body: { archived: false },
      });

      if (response.ok && response.data) {
        const data = UpdateQuoteResponseSchema.parse(response.data);

        // Stale responses skip the related merges too (see getQuote).
        if (!isStaleWrite(get().quotes.get(id), data.quote)) {
          mergeRelatedEntities(data.quote);
        }

        set((state) => {
          const quotes = new Map(state.quotes);
          const quoteLoadingStates = new Map(state.quoteLoadingStates);
          if (!isStaleWrite(quotes.get(id), data.quote)) {
            quotes.set(id, data.quote);
          }
          quoteLoadingStates.set(id, getInitialLoadingState());
          return { quotes, quoteLoadingStates };
        });
        return { success: true };
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        "Failed to unarchive quote";
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: errorMessage,
        });
        return { quoteLoadingStates };
      });
      return { success: false, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteLoadingStates = new Map(state.quoteLoadingStates);
        const currentState =
          quoteLoadingStates.get(id) || getInitialLoadingState();
        quoteLoadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to unarchive quote",
        });
        return { quoteLoadingStates };
      });
      return { success: false, error: "Failed to unarchive quote" };
    }
  },
}));
